/**
 * EmotionTab Component
 *
 * Dedicated tab for emotion perception configuration.
 * Configures the emotion_enhance hook which analyzes user emotions and intents.
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import { Heart, Zap, TrendingUp, MessageSquare, ShieldAlert, Info } from 'lucide-react';
import { getConfigValue } from '@/config/fieldConfig';
import type { AgentConfiguration } from '@/types';

interface EmotionTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
}

export const EmotionTab: React.FC<EmotionTabProps> = ({
  config,
  onChange,
  appContext: _appContext,
}) => {
  const isEnabled = getConfigValue(config, 'hooks.emotion_enhance.enabled') === true;
  const includeIntents = getConfigValue(config, 'hooks.emotion_enhance.include_intents') ?? true;
  const includeEmotions = getConfigValue(config, 'hooks.emotion_enhance.include_emotions') ?? true;
  const includeGuidance = getConfigValue(config, 'hooks.emotion_enhance.include_guidance') ?? true;
  const includeTrend = getConfigValue(config, 'hooks.emotion_enhance.include_trend') ?? true;
  const fallbackOnError = getConfigValue(config, 'hooks.emotion_enhance.fallback_on_error') ?? true;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">情绪感知</h2>
          <p className="text-sm text-slate-500 mt-1">
            分析用户消息的情绪和意图，增强 Agent 的理解能力
          </p>
        </div>
      </div>

      {/* Main Enable Toggle */}
      <Card className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${isEnabled ? 'bg-pink-100' : 'bg-slate-100'}`}>
              <Heart className={`w-6 h-6 ${isEnabled ? 'text-pink-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">启用情绪感知</h3>
              <p className="text-sm text-slate-500 mt-1">
                调用 Emotion Perception Service 分析用户消息，将分析结果注入到上下文中
              </p>
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>注入内容示例：</strong>
                </p>
                <code className="text-xs text-slate-500 block mt-1 font-mono">
                  {'<user_perspective>'}
                  <br />
                  {'  情绪: 焦虑 (0.72), 期待 (0.45)'}
                  <br />
                  {'  意图: 寻求建议, 情感支持'}
                  <br />
                  {'</user_perspective>'}
                </code>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange('hooks.emotion_enhance.enabled', !isEnabled)}
            className={`
              relative w-14 h-7 rounded-full transition-colors flex-shrink-0
              ${isEnabled ? 'bg-pink-500' : 'bg-slate-300'}
            `}
          >
            <div
              className={`
                absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform
                ${isEnabled ? 'translate-x-8' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </Card>

      {/* Analysis Options */}
      <Card className={`p-6 ${!isEnabled ? 'opacity-50' : ''}`}>
        <div className="flex items-start gap-4 mb-4">
          <div className={`p-3 rounded-xl ${isEnabled ? 'bg-purple-100' : 'bg-slate-100'}`}>
            <Zap className={`w-6 h-6 ${isEnabled ? 'text-purple-600' : 'text-slate-400'}`} />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">分析选项</h3>
            <p className="text-sm text-slate-500 mt-1">
              选择要包含的分析类型
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 ml-16">
          {/* Include Emotions */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Heart className={`w-4 h-4 ${includeEmotions && isEnabled ? 'text-pink-500' : 'text-slate-400'}`} />
              <div>
                <span className="text-sm font-medium text-slate-700">情绪分析</span>
                <p className="text-xs text-slate-400">识别用户的情绪状态</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => isEnabled && onChange('hooks.emotion_enhance.include_emotions', !includeEmotions)}
              disabled={!isEnabled}
              className={`
                relative w-10 h-5 rounded-full transition-colors
                ${includeEmotions && isEnabled ? 'bg-pink-500' : 'bg-slate-300'}
                ${!isEnabled ? 'cursor-not-allowed' : ''}
              `}
            >
              <div
                className={`
                  absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
                  ${includeEmotions && isEnabled ? 'translate-x-5' : 'translate-x-0.5'}
                `}
              />
            </button>
          </div>

          {/* Include Intents */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <MessageSquare className={`w-4 h-4 ${includeIntents && isEnabled ? 'text-blue-500' : 'text-slate-400'}`} />
              <div>
                <span className="text-sm font-medium text-slate-700">意图分析</span>
                <p className="text-xs text-slate-400">识别用户的对话意图</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => isEnabled && onChange('hooks.emotion_enhance.include_intents', !includeIntents)}
              disabled={!isEnabled}
              className={`
                relative w-10 h-5 rounded-full transition-colors
                ${includeIntents && isEnabled ? 'bg-blue-500' : 'bg-slate-300'}
                ${!isEnabled ? 'cursor-not-allowed' : ''}
              `}
            >
              <div
                className={`
                  absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
                  ${includeIntents && isEnabled ? 'translate-x-5' : 'translate-x-0.5'}
                `}
              />
            </button>
          </div>

          {/* Include Guidance */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Zap className={`w-4 h-4 ${includeGuidance && isEnabled ? 'text-amber-500' : 'text-slate-400'}`} />
              <div>
                <span className="text-sm font-medium text-slate-700">响应指导</span>
                <p className="text-xs text-slate-400">提供回复建议</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => isEnabled && onChange('hooks.emotion_enhance.include_guidance', !includeGuidance)}
              disabled={!isEnabled}
              className={`
                relative w-10 h-5 rounded-full transition-colors
                ${includeGuidance && isEnabled ? 'bg-amber-500' : 'bg-slate-300'}
                ${!isEnabled ? 'cursor-not-allowed' : ''}
              `}
            >
              <div
                className={`
                  absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
                  ${includeGuidance && isEnabled ? 'translate-x-5' : 'translate-x-0.5'}
                `}
              />
            </button>
          </div>

          {/* Include Trend */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <TrendingUp className={`w-4 h-4 ${includeTrend && isEnabled ? 'text-green-500' : 'text-slate-400'}`} />
              <div>
                <span className="text-sm font-medium text-slate-700">情绪趋势</span>
                <p className="text-xs text-slate-400">追踪情绪变化</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => isEnabled && onChange('hooks.emotion_enhance.include_trend', !includeTrend)}
              disabled={!isEnabled}
              className={`
                relative w-10 h-5 rounded-full transition-colors
                ${includeTrend && isEnabled ? 'bg-green-500' : 'bg-slate-300'}
                ${!isEnabled ? 'cursor-not-allowed' : ''}
              `}
            >
              <div
                className={`
                  absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
                  ${includeTrend && isEnabled ? 'translate-x-5' : 'translate-x-0.5'}
                `}
              />
            </button>
          </div>
        </div>
      </Card>

      {/* Error Handling */}
      <Card className={`p-6 ${!isEnabled ? 'opacity-50' : ''}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${fallbackOnError && isEnabled ? 'bg-green-100' : 'bg-slate-100'}`}>
              <ShieldAlert className={`w-6 h-6 ${fallbackOnError && isEnabled ? 'text-green-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">错误降级</h3>
              <p className="text-sm text-slate-500 mt-1">
                当情绪服务不可用时，继续处理请求（不包含情绪分析）
              </p>
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                <Info className="w-3 h-3" />
                建议保持开启，避免服务故障影响主流程
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => isEnabled && onChange('hooks.emotion_enhance.fallback_on_error', !fallbackOnError)}
            disabled={!isEnabled}
            className={`
              relative w-14 h-7 rounded-full transition-colors flex-shrink-0
              ${fallbackOnError && isEnabled ? 'bg-green-500' : 'bg-slate-300'}
              ${!isEnabled ? 'cursor-not-allowed' : ''}
            `}
          >
            <div
              className={`
                absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform
                ${fallbackOnError && isEnabled ? 'translate-x-8' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </Card>

      {/* Status Summary */}
      {isEnabled && (
        <Card className="p-4 bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 border-pink-100">
          <div className="flex items-center gap-3">
            <Heart className="w-5 h-5 text-pink-600" />
            <div>
              <h4 className="text-sm font-medium text-pink-900">当前启用的分析</h4>
              <div className="flex gap-2 mt-2 flex-wrap">
                {includeEmotions && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-pink-100 text-pink-700 rounded-full">
                    情绪分析
                  </span>
                )}
                {includeIntents && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                    意图分析
                  </span>
                )}
                {includeGuidance && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-full">
                    响应指导
                  </span>
                )}
                {includeTrend && (
                  <span className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                    情绪趋势
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Help Text */}
      <Card className="p-4 bg-slate-50 border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 mb-2">情绪感知说明</h4>
        <ul className="text-xs text-slate-600 space-y-1">
          <li>• <strong>情绪分析</strong>：识别用户消息中的情绪状态（如焦虑、期待、沮丧等）及其强度</li>
          <li>• <strong>意图分析</strong>：理解用户的对话目的（如寻求建议、情感支持、信息查询等）</li>
          <li>• <strong>响应指导</strong>：基于情绪和意图提供回复建议，帮助 Agent 更好地响应</li>
          <li>• <strong>情绪趋势</strong>：追踪对话过程中的情绪变化，识别情绪转折点</li>
          <li>• 分析结果以 XML 格式注入到用户消息上下文中</li>
          <li>• 使用 Circuit Breaker 模式保护，连续失败后自动熔断</li>
        </ul>
      </Card>
    </div>
  );
};

export default EmotionTab;
